/**
 * Slow Load Service
 * 
 * Centralized service for tracking slow loading operations.
 * Sends data to Sentry (warnings/errors + breadcrumbs), Amplitude (separate events),
 * and logs to app_logs via logger.
 */

import * as Sentry from '@sentry/react';
import { logger } from './logger';
import { analyticsService } from './analyticsService';

const DEDUP_WINDOW_MS = 60_000; // 1 minute between same warnings
const slowLoadDedup = new Map<string, number>();

let totalSlowLoads = 0;
let warningCount = 0;
let errorCount = 0;

export interface SlowLoadOptions {
  /** Name of the operation being monitored */
  operationName: string;
  /** Actual duration in milliseconds */
  durationMs: number;
  /** Threshold that was exceeded */
  thresholdMs: number;
  /** Screen ID for analytics */
  screenId?: string;
  /** Current route */
  route?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

function getDedupKey(type: string, operationName: string): string {
  return `${type}:${operationName}:${window.location.pathname}`;
}

function shouldReport(key: string): boolean {
  const now = Date.now();
  const last = slowLoadDedup.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return false;
  slowLoadDedup.set(key, now);
  return true;
}

/**
 * Track a slow load event
 * @param type - 'warning' for slow but acceptable, 'error' for critical delays
 * @param options - Details about the slow load
 */
export function trackSlowLoad(
  type: 'warning' | 'error',
  options: SlowLoadOptions
): void {
  const { 
    operationName, 
    durationMs, 
    thresholdMs, 
    screenId, 
    route, 
    metadata 
  } = options;
  
  totalSlowLoads++;
  if (type === 'warning') warningCount++;
  if (type === 'error') errorCount++;
  
  const dedupKey = getDedupKey(type, operationName);
  const shouldLog = shouldReport(dedupKey);
  
  const currentRoute = route || window.location.pathname;
  
  const eventData = {
    operation: operationName,
    duration_ms: durationMs,
    threshold_ms: thresholdMs,
    route: currentRoute,
    exceeded_by_ms: durationMs - thresholdMs,
    ...metadata,
  };
  
  // 1. Always add Sentry breadcrumb for context
  Sentry.addBreadcrumb({
    category: 'performance',
    message: `Slow load ${type}: ${operationName} (${durationMs}ms > ${thresholdMs}ms)`,
    level: type === 'error' ? 'error' : 'warning',
    data: eventData,
  });
  
  if (!shouldLog) return; // Deduplicate repeated warnings
  
  // 2. Send to Sentry as warning/error message
  Sentry.captureMessage(`Slow load: ${operationName} (${type})`, {
    level: type === 'error' ? 'error' : 'warning',
    tags: {
      operation: operationName,
      slow_load_type: type,
      route: currentRoute,
    },
    extra: eventData,
  });
  
  // 3. Log via logger (→ app_logs table)
  const logContext = {
    context: { 
      feature: 'performance', 
      screen_id: screenId, 
      route: currentRoute,
    },
    data: eventData,
    allowList: ['operation', 'duration_ms', 'threshold_ms', 'route', 'exceeded_by_ms'],
  };
  
  if (type === 'error') {
    logger.error(`Slow load error: ${operationName} took ${durationMs}ms (threshold: ${thresholdMs}ms)`, {
      ...logContext,
      error_code: 'slow_load_error',
      error_type: 'ui',
    });
  } else {
    logger.warn(`Slow load warning: ${operationName} took ${durationMs}ms (threshold: ${thresholdMs}ms)`, logContext);
  }
  
  // 4. Send SEPARATE event to Amplitude (not just batch)
  analyticsService.track(`slow_load_${type}`, eventData, screenId);
}

/**
 * Track when a load completes (for measuring actual durations)
 */
export function trackLoadComplete(
  operationName: string,
  durationMs: number,
  hadWarning: boolean,
  hadError: boolean,
  screenId?: string
): void {
  const eventData = {
    operation: operationName,
    duration_ms: durationMs,
    had_warning: hadWarning,
    had_error: hadError,
    route: window.location.pathname,
  };
  
  // Add breadcrumb for context
  Sentry.addBreadcrumb({
    category: 'performance',
    message: `Load complete: ${operationName} (${durationMs}ms)`,
    level: 'info',
    data: eventData,
  });
  
  // Track in Amplitude for performance analysis
  analyticsService.track('load_complete', eventData, screenId);
}

/**
 * Get current slow load statistics
 */
export function getSlowLoadStats(): { 
  total: number; 
  warnings: number; 
  errors: number;
  uniqueOperations: number;
} {
  return {
    total: totalSlowLoads,
    warnings: warningCount,
    errors: errorCount,
    uniqueOperations: slowLoadDedup.size,
  };
}

/**
 * Clear statistics (useful for testing)
 */
export function clearSlowLoadStats(): void {
  totalSlowLoads = 0;
  warningCount = 0;
  errorCount = 0;
  slowLoadDedup.clear();
}

export const slowLoadService = {
  trackWarning: (options: SlowLoadOptions) => trackSlowLoad('warning', options),
  trackError: (options: SlowLoadOptions) => trackSlowLoad('error', options),
  trackComplete: trackLoadComplete,
  getStats: getSlowLoadStats,
  clearStats: clearSlowLoadStats,
};
